// ---------------------------------------------------------------------------
// DrawerPaneLoadingLayout — estado de carregamento padrao das panes V3.
// ---------------------------------------------------------------------------
// Titulo fixo no topo + mensagem + spinner ``Grid`` (``react-loader-spinner``),
// alinhado ao restante da aplicacao — mesmas dimensoes (32 px) e cor da
// identidade visual (``clientColor``, fallback tema).
//
// Tambem exportamos ``PaneLoadingBody`` para reuso em superficies
// menores (ex.: aba Log dentro de um bloco) sem duplicar markup.
// ---------------------------------------------------------------------------

import React from "react";
import PropTypes from "prop-types";
import { Grid } from "react-loader-spinner";

import { dangerColor } from "../../../assets/jss/material-dashboard-pro-react";
import useDrawerStyles from "./drawer/styles";
import useProgressStyles from "./progress_drawer/styles";
import useThemeColors from "../../../hooks/useThemeColors";

const GRID_LOADER_PX = 32;

export function PaneLoadingBody({
  classes,
  clientColor,
  message,
  testId,
  slotClass,
}) {
  const gridColor = clientColor || dangerColor[2];
  return (
    <div
      className={slotClass || classes.paneLoading}
      role="status"
      aria-live="polite"
      data-testid={testId}
    >
      <div className={classes.paneLoadingGridWrap}>
        <Grid
          width={GRID_LOADER_PX}
          height={GRID_LOADER_PX}
          color={gridColor}
          visible
          ariaLabel={message}
        />
      </div>
      <div className={classes.paneLoadingLabel}>{message}</div>
    </div>
  );
}

PaneLoadingBody.propTypes = {
  classes: PropTypes.object.isRequired,
  clientColor: PropTypes.string,
  message: PropTypes.string.isRequired,
  testId: PropTypes.string,
  slotClass: PropTypes.string,
};

/**
 * @param {"input"|"progress"} variant
 * @param {string} title
 * @param {string} [headCount] — apenas variant ``progress`` (``paneHeadCount``)
 * @param {string} message
 * @param {React.Ref} [scrollRef] — variant ``input`` (``useNativeWheelScroll``)
 * @param {string} [overlayScrollClass] — classes overlay do scroll (input)
 */
const DrawerPaneLoadingLayout = ({
  variant,
  title,
  headCount,
  message,
  scrollRef,
  overlayScrollClass,
}) => {
  const { clientColor } = useThemeColors();
  const drawerClasses = useDrawerStyles({ clientColor });
  const progressClasses = useProgressStyles();

  if (variant === "progress") {
    return (
      <>
        <div className={progressClasses.paneHeadLoading}>
          <h4 className={progressClasses.paneHeadTitle}>{title}</h4>
          {headCount != null && headCount !== "" ? (
            <span className={progressClasses.paneHeadCount}>{headCount}</span>
          ) : null}
        </div>
        <PaneLoadingBody
          classes={progressClasses}
          clientColor={clientColor}
          message={message}
          slotClass={progressClasses.paneLoadingLayoutSlot}
        />
      </>
    );
  }

  const scrollCls = [
    drawerClasses.paneScroll,
    drawerClasses.paneScrollFlexColumn,
    overlayScrollClass || "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div ref={scrollRef} className={scrollCls}>
      <div className={drawerClasses.paneLoadingLayoutHead}>
        <div className={drawerClasses.paneLoadingLayoutTitle}>{title}</div>
      </div>
      <PaneLoadingBody
        classes={drawerClasses}
        clientColor={clientColor}
        message={message}
        slotClass={drawerClasses.paneLoadingLayoutSlot}
      />
    </div>
  );
};

DrawerPaneLoadingLayout.propTypes = {
  variant: PropTypes.oneOf(["input", "progress"]).isRequired,
  title: PropTypes.string.isRequired,
  headCount: PropTypes.string,
  message: PropTypes.string.isRequired,
  scrollRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  overlayScrollClass: PropTypes.string,
};

export default DrawerPaneLoadingLayout;
